import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { loginWithPromise } from '../../store/slice/authSlice';

export default function AutoRetryControl({ onClose }) {
    const [agencies, setAgencies] = useState([]);
    const [originalAgencies, setOriginalAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAgencies();
    }, []);

    const fetchAgencies = () => {
        setLoading(true);
        setError(null);
        loginWithPromise().then((token) => {
            axios.get(process.env.REACT_APP_paymentBaseUrl + 'retry-agencies', {
                headers: { Authorization: token }
            }).then((resp) => {
                const items = (resp.data && resp.data.data) ? resp.data.data : [];
                const agenciesCopy = items.map((a) => ({ ...a }));
                setAgencies(agenciesCopy);
                setOriginalAgencies(agenciesCopy.map((a) => ({ ...a })));
                setLoading(false);
            }).catch((err) => {
                console.error('Failed to fetch retry agencies', err);
                setError('Failed to load agencies. Please try again.');
                setLoading(false);
            });
        }).catch((err) => {
            console.error('Auth error while fetching agencies', err);
            setError('Authentication failed. Please refresh and try again.');
            setLoading(false);
        });
    };

    const handleToggle = (index) => {
        setAgencies((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, retryEnabled: !item.retryEnabled } : item
            )
        );
    };

    const handleConfirm = () => {
        setSaving(true);
        setError(null);
        const updatedBy = localStorage.getItem('email') || 'admin';
        
        // Only include agencies where retryEnabled has changed
        const payload = agencies
            .filter((agency) => {
                const original = originalAgencies.find((a) => a.clientId === agency.clientId);
                return original && original.retryEnabled !== agency.retryEnabled;
            })
            .map((agency) => ({
                clientId: agency.clientId,
                retryEnabled: agency.retryEnabled,
                updatedBy
            }));

        // If no changes, just close the modal
        if (payload.length === 0) {
            setSaving(false);
            onClose();
            return;
        }

        loginWithPromise().then((token) => {
            axios.put(
                process.env.REACT_APP_paymentBaseUrl + 'retry-agencies/status',
                payload,
                { headers: { Authorization: token, 'Content-Type': 'application/json' } }
            ).then(() => {
                setSaving(false);
                onClose();
            }).catch((err) => {
                console.error('Failed to update retry agencies', err);
                setError('Failed to save changes. Please try again.');
                setSaving(false);
            });
        }).catch((err) => {
            console.error('Auth error while updating agencies', err);
            setError('Authentication failed. Please refresh and try again.');
            setSaving(false);
        });
    };

    return (
        <div className="auto-retry-modal-overlay" role="dialog" aria-modal="true">
            <div className="auto-retry-modal">
                <div className="auto-retry-modal-header">
                    <h4 className="auto-retry-modal-title">Auto-Retry Batch Control</h4>
                    <button
                        type="button"
                        className="auto-retry-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="auto-retry-table-header">
                    <span>AGENCY</span>
                    <span>ENABLE</span>
                </div>

                <div className="auto-retry-list-body">
                    {loading && (
                        <div className="auto-retry-status-row">Loading agencies…</div>
                    )}
                    {!loading && error && (
                        <div className="auto-retry-status-row auto-retry-error-text">{error}</div>
                    )}
                    {!loading && !error && agencies.length === 0 && (
                        <div className="auto-retry-status-row">No agencies found.</div>
                    )}
                    {!loading && !error && agencies.map((agency, index) => (
                        <div className="auto-retry-row" key={agency.clientId}>
                            <span className="auto-retry-agency-name">{agency.agencyName}</span>
                            <label className="auto-retry-switch">
                                <input
                                    type="checkbox"
                                    checked={agency.retryEnabled}
                                    onChange={() => handleToggle(index)}
                                />
                                <span className="auto-retry-slider" />
                            </label>
                        </div>
                    ))}
                </div>

                {error && !loading && (
                    <div className="auto-retry-error-footer">{error}</div>
                )}

                <div className="auto-retry-actions">
                    <button
                        type="button"
                        className="auto-retry-btn auto-retry-btn-secondary"
                        onClick={onClose}
                        disabled={saving}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        className="auto-retry-btn auto-retry-btn-primary"
                        onClick={handleConfirm}
                        disabled={saving || loading}
                    >
                        {saving ? 'SAVING…' : 'CONFIRM'}
                    </button>
                </div>
            </div>
        </div>
    );
}
